<script setup>
import { onBeforeUnmount, watch } from "vue"
import { router, usePage } from "@inertiajs/vue3"
import Toast from "@/components/feedback/Toast.vue"
import { toast, useToastQueue } from "@/composables/useToast"

// Singleton host. Mount it once in `application.js` (alongside the
// active Inertia App) — never embed it inside a page or layout, or
// the queue/animations will break across navigations.

const { items, dismiss } = useToastQueue()
const page = usePage()

// Forward server-rendered flashes (`flash.notice` / `flash.alert` / `flash.info`)
// into the toast queue. Watching the reactive flash object means
// every Inertia navigation that carries a flash triggers a toast
// without each page importing or rendering anything.
//
// De-duplicate by content signature on partial reloads only: pages
// with chat-style polling (e.g. `OrderChatPanel`) may replay the
// same flash payload Inertia already toasted on the previous
// render. Full visits (form submits, redirects) reset the guard so
// identical messages — e.g. repeated login failures — still toast.
let lastFlashSignature = ""

const removeStartListener = router.on("start", (event) => {
  const only = event.detail.visit.only
  if (!only?.length) {
    lastFlashSignature = ""
  }
})

onBeforeUnmount(() => removeStartListener())

watch(
  () => page.props.flash,
  (flash) => {
    if (!flash) return
    const signature = `${flash.notice ?? ""}\u0000${flash.alert ?? ""}\u0000${flash.info ?? ""}`
    if (signature === "\u0000\u0000\u0000" || signature === lastFlashSignature) return
    lastFlashSignature = signature
    if (flash.notice) toast.success(flash.notice)
    if (flash.alert) toast.error(flash.alert)
    if (flash.info) toast.info(flash.info)
  },
  { immediate: true, deep: true },
)
</script>

<template>
  <Teleport to="body">
    <div
      class="pointer-events-none fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-3"
      aria-live="polite"
      aria-atomic="false"
    >
      <TransitionGroup
        tag="div"
        class="flex flex-col items-center gap-3"
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="-translate-y-3 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-200 ease-in absolute"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="-translate-y-3 opacity-0"
        move-class="transition-transform duration-200 ease-out"
      >
        <Toast
          v-for="t in items"
          :key="t.id"
          :variant="t.variant"
          :message="t.message"
          @dismiss="dismiss(t.id)"
        />
      </TransitionGroup>
    </div>
  </Teleport>
</template>
